import { ForwardedRef, forwardRef } from "react";
import { FieldPath, FieldValues } from "react-hook-form";
import { Select, SelectProps } from "../forms/select";
import { FormField, TFormField } from "./form-field";

interface SelectFormFieldProps<
  TValue = string,
  TFieldValues extends FieldValues = FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends TFormField<TFieldValues, TFieldPath>,
    Omit<SelectProps, "name" | "size" | "onChange"> {
  options: SelectOptions<TValue>;
}

export interface SelectGroup<TValue> {
  label: string;
  children: SelectOption<TValue>[];
}

export interface SelectOption<TValue> {
  label: string;
  value: TValue;
  disabled?: boolean;
}

export type SelectOptions<TValue> = (
  | SelectOption<TValue>
  | SelectGroup<TValue>
)[];

export const SelectFormField = forwardRef(function SelectFormField<
  TValue = string,
  TFieldValues extends FieldValues = FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  {
    name,
    size,
    options,
    ...props
  }: SelectFormFieldProps<TValue, TFieldValues, TFieldPath>,
  ref: ForwardedRef<HTMLSelectElement>,
) {
  return (
    <FormField name={name} size={size} {...props}>
      {(field) => (
        <Select
          ref={ref}
          {...field}
          required={props.required}
          onChange={(event) =>
            field.onChange({
              ...event,
              target: {
                ...event.target,
                value: getOptionValue(options, event.target.value),
              },
            })
          }
        >
          <Options options={options} />
        </Select>
      )}
    </FormField>
  );
});

const Options = <TValue,>({ options }: { options: SelectOptions<TValue> }) =>
  options.map((option) =>
    isGroup(option) ? (
      <optgroup key={`${option.label}_group`} label={option.label}>
        <Options options={option.children} />
      </optgroup>
    ) : (
      <option
        key={`${option.label}-${option.value}`}
        value={String(option.value)}
        disabled={option.disabled}
      >
        {option.label}
      </option>
    ),
  );

const getOptionValue = <TValue,>(
  options: SelectOptions<TValue>,
  value: string,
): TValue | undefined => {
  for (const option of options) {
    if (isGroup(option)) {
      const result = getOptionValue(option.children, value);
      if (result !== undefined) return result;
    } else if (value === String(option.value)) {
      return option.value;
    }
  }
  return undefined;
};

const isGroup = <TValue,>(
  option: SelectOption<TValue> | SelectGroup<TValue>,
): option is SelectGroup<TValue> => "children" in option;
